import { type MenuContentCategoryWithSlug, type MenuContentProduct } from '../../MenuContent.types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Flattens menu categories into list rows: a heading and one row per product,
 * with the last product closing the category spacing. Custom categories
 * collapse into a single row that lays out its own cards. Dense
 * categories render their products in pairs, one row per pair.
 */
export function buildMenuContentListRows(params: Params): MenuContentListRow[] {
  const { categories, reorderRailTargetId, denseCategoryIds } = params;

  const reorderRailRows = reorderRailTargetId ? [buildReorderRailRow(reorderRailTargetId)] : [];
  const categoryRows = categories.flatMap((category, categoryIndex) =>
    buildCategoryRows(category, categoryIndex, denseCategoryIds),
  );

  return [...reorderRailRows, ...categoryRows];
}

export function getMenuContentListRowTargetId(row: MenuContentListRow) {
  return row.targetId;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function buildReorderRailRow(targetId: string): MenuContentListRow {
  return { kind: 'reorder-rail', key: targetId, targetId };
}

function buildCategoryRows(
  category: MenuContentCategoryWithSlug,
  categoryIndex: number,
  denseCategoryIds: Set<string> | undefined,
): MenuContentListRow[] {
  const rowBase: CategoryRowBase = { key: category.slug, targetId: category.slug, category, categoryIndex }; // prettier-ignore

  if (category.isCustom) return buildCustomCategoryRows(rowBase);

  if (denseCategoryIds?.has(category.id)) return buildDenseCategoryRows(rowBase);

  const headingRow = buildCategoryHeadingRow(rowBase);
  const lastProductIndex = category.products.length - 1;
  const productRows = category.products.map<MenuContentListRow>((product, productIndex) => ({
    ...rowBase,
    kind: 'product',
    key: `${rowBase.key}:product:${product.id}`,
    product,
    isLastProduct: productIndex === lastProductIndex,
  }));

  return [headingRow, ...productRows];
}

function buildDenseCategoryRows(rowBase: CategoryRowBase): MenuContentListRow[] {
  const productPairs = chunkPairs(rowBase.category.products);
  const lastPairIndex = productPairs.length - 1;
  const pairRows = productPairs.map<MenuContentListRow>((products, pairIndex) => ({
    ...rowBase,
    kind: 'dense-product-pair',
    key: `${rowBase.key}:dense:${products[0]?.id ?? pairIndex}`,
    products,
    isLastRow: pairIndex === lastPairIndex,
  }));

  return [buildCategoryHeadingRow(rowBase), ...pairRows];
}

function buildCategoryHeadingRow(rowBase: CategoryRowBase): MenuContentListRow {
  return { ...rowBase, kind: 'category-heading', key: `${rowBase.key}:heading` };
}

function chunkPairs<T>(items: readonly T[]): T[][] {
  return Array.from({ length: Math.ceil(items.length / 2) }, (_, pairIndex) =>
    items.slice(pairIndex * 2, pairIndex * 2 + 2),
  );
}

function buildCustomCategoryRows(rowBase: CategoryRowBase): MenuContentListRow[] {
  const customProducts = rowBase.category.products.filter((product) => product.isCustom);

  if (customProducts.length === 0) return [];

  return [{ ...rowBase, kind: 'custom-products', key: `${rowBase.key}:custom`, products: customProducts }]; // prettier-ignore
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  categories: readonly MenuContentCategoryWithSlug[];
  reorderRailTargetId: string | undefined;
  denseCategoryIds?: Set<string>;
};

type RowBase = { key: string; targetId: string };

type CategoryRowBase = RowBase & {
  category: MenuContentCategoryWithSlug;
  categoryIndex: number;
};

export type MenuContentListRow =
  | (RowBase & { kind: 'reorder-rail' })
  | (CategoryRowBase & { kind: 'category-heading' })
  | (CategoryRowBase & { kind: 'product'; product: MenuContentProduct; isLastProduct: boolean })
  | (CategoryRowBase & { kind: 'custom-products'; products: MenuContentProduct[] })
  | (CategoryRowBase & { kind: 'dense-product-pair'; products: MenuContentProduct[]; isLastRow: boolean }); // prettier-ignore
